import { Dog, Horse as HorseIcon } from '@phosphor-icons/react';
import { Horse, Product, ProductTypeEnum } from 'openapi';
import { SexEnum } from 'openapi';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FilterSelectButton, { FilterSelectButtonGrouped } from 'components/Common/ListFilter/FilterSelectButton';
import { informalHorseName, textFilter } from 'utilities/Horse';
import { useOrganization } from 'context/OrganizationContext';

interface Props {
  horses: Horse[];
  horseSelected: (horse?: Horse) => void;
  selectedHorse?: Horse;
  stallionsOnly?: boolean;
  loading?: boolean;
  // this flag make sure that the text is not too long on mobile
  reduceSpaceOnMobile?: boolean;
  // if given, we will show a divided list of stallions, one for the products and one for the other stallions
  products?: Product[];
}

// A button that show a dropdown (or modal) of the HorseSelect.
export default function HorseSelectButton({
  horses,
  horseSelected,
  stallionsOnly,
  selectedHorse,
  loading,
  reduceSpaceOnMobile,
  products,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const { selectedOrganization } = useOrganization();

  const filtered = useMemo(() => {
    let res = horses;
    if (stallionsOnly) {
      res = horses.filter(horse => horse.sex === SexEnum._1);
    }
    return res;
  }, [horses, stallionsOnly]);

  const title = useMemo(() => {
    return stallionsOnly ? t('select-stallion', 'Select a stallion') : t('select-horse', 'Select a horse');
  }, [stallionsOnly, t]);

  const buttonText = useMemo(() => {
    return stallionsOnly ? t('choose-stallion', 'Choose a stallion') : t('choose-horse', 'Choose a horse');
  }, [stallionsOnly, t]);

  /**
   * Build the list of stallions to display in the selectInput component, divided in two parts:
   * 1. The stallion products, which are the products.
   * 2. The other stallions, which are the horses that are not products.
   */
  const groupedStallions = useMemo(() => {
    const stallionProducts = filtered.filter(horse =>
      products?.find(product => product.stallion_uid === horse.uid && product.product_type === ProductTypeEnum.BREEDING),
    );
    const options: FilterSelectButtonGrouped<Horse>[] = [
      {
        heading: t('stallion-products', 'Stallion products'),
        items: stallionProducts ?? [],
      },
    ];

    const otherStallions = filtered.filter(horse => !stallionProducts?.some(stallion => stallion.uid === horse.uid));
    if (otherStallions.length > 0) {
      options.push({
        heading: t('other-stallions', 'Other Stallions'),
        items: otherStallions,
      });
    }
    return options;
  }, [filtered, products, t]);

  return (
    <FilterSelectButton<Horse>
      options={stallionsOnly ? groupedStallions : filtered}
      display={horse => informalHorseName(horse) ?? '-'}
      idField='uid'
      onFilter={textFilter}
      onSelected={selected => horseSelected(selected)}
      selectedOption={selectedHorse}
      title={title}
      buttonText={buttonText}
      icon={selectedOrganization?.is_ssd ? <Dog /> : <HorseIcon />}
      loading={loading}
      reduceSpaceOnMobile={reduceSpaceOnMobile}
    />
  );
}
